// Save status (0.6.1): what the advisor needs to trust local work — every change is written to this device's store in
// the same transaction before the screen updates; this module only reports it. Also: whether the browser keeps the
// data (persistent storage), whether the network is there, when the last backup file was made, and the automatic
// recovery snapshots (taken at most every 12 hours when the data changed; the newest 7 are kept).
import { useSyncExternalStore } from 'react';
import type { Workspace } from '@vero/store';
import { autoSnapshot } from '@vero/backup';

export interface SaveState {
  /** last successful write to the device store (ISO) — null until the first change of this session */
  savedAt: string | null;
  /** a write failed (the change was not applied) */
  error: string | null;
  /** running in memory only (storage unavailable): nothing survives closing the page */
  volatile: boolean;
  /** navigator.storage.persisted(): true = the browser will not evict the data under storage pressure */
  persisted: boolean | null;
  online: boolean;
  /** last backup file exported on this device (ISO) */
  lastExport: string | null;
  /** newest automatic recovery snapshot (ISO) */
  lastAuto: string | null;
}

let state: SaveState = { savedAt: null, error: null, volatile: false, persisted: null, online: typeof navigator === 'undefined' ? true : navigator.onLine !== false, lastExport: null, lastAuto: null };
const listeners = new Set<() => void>();
const set = (p: Partial<SaveState>) => { state = { ...state, ...p }; for (const l of listeners) l(); };
let key = 'vero.lastBackupExport';

function readLocal(k: string): string | null { try { return localStorage.getItem(k); } catch { return null; } }
function writeLocal(k: string, v: string) { try { localStorage.setItem(k, v); } catch { /* storage blocked: the status just stays unknown */ } }

export function initSaveStatus(ws: Workspace, o: { volatile: boolean; dbName?: string; autoSnapshots?: boolean }) {
  key = `vero.lastBackupExport.${o.dbName ?? 'vero-os'}`;
  set({ volatile: o.volatile, lastExport: readLocal(key) });
  ws.subscribe(() => set({ savedAt: new Date().toISOString(), error: null }));
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => set({ online: true }));
    window.addEventListener('offline', () => set({ online: false }));
  }
  const st = (typeof navigator !== 'undefined' ? (navigator as Navigator & { storage?: StorageManager }).storage : undefined);
  if (!o.volatile && st?.persist) {
    // ask once; browsers decide (Chrome/Edge grant it for sites the person uses, installed apps and local files)
    void st.persisted().then((p) => (p ? true : st.persist())).then((p) => set({ persisted: !!p })).catch(() => set({ persisted: null }));
  }
  if (!o.volatile && o.autoSnapshots !== false) {
    const tick = async () => {
      try {
        const made = await autoSnapshot(ws);
        const list = await ws.adapter.listSnapshots();
        const newest = list.filter((s) => s.reason === 'auto').sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
        set({ lastAuto: made?.createdAt ?? newest?.createdAt ?? null });
      } catch (e) { console.warn('[auto-snapshot]', e); }
    };
    setTimeout(() => void tick(), 8_000);          // shortly after start, off the critical path
    setInterval(() => void tick(), 60 * 60_000);   // then hourly checks (a snapshot is only taken every 12 h at most)
  }
}

export function noteSaveError(e: unknown) { set({ error: String((e as Error)?.message ?? e) }); }
export function markBackupExport(at = new Date().toISOString()) { writeLocal(key, at); set({ lastExport: at }); }
export function saveStatus(): SaveState { return state; }
export function useSaveStatus(): SaveState { return useSyncExternalStore((cb) => { listeners.add(cb); return () => listeners.delete(cb); }, () => state); }
/** days since the last backup file (null = never) */
export function backupAgeDays(s: SaveState, now = Date.now()): number | null { return s.lastExport ? Math.floor((now - Date.parse(s.lastExport)) / 86_400_000) : null; }
